import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../api/api";
import "./Tours-title-ellipsis.css";


export default function History() {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [filter, setFilter] = useState("All");

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return date.toLocaleDateString("en-GB", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric"
    });
  };

  const formatTime = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    });
  };

  const formatRate = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    return new Intl.NumberFormat("en-IE", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 2
    }).format(Number(value) || 0);
  };

  useEffect(() => {
    API.get("/guide/history")
      .then(res => setHistory(Array.isArray(res.data) ? res.data : []))
      .catch(err => console.error("history error:", err));
  }, []);

  const getStatus = (h) => {
    const endTime = new Date(h.end_datetime || h.start_datetime);
    return h.unassigned_at || endTime < new Date() ? "Completed" : "Active";
  };

  const filteredHistory = useMemo(() => {
    if (filter === "All") return history;
    return history.filter(h => getStatus(h) === filter);
  }, [history, filter]);

  return (
    <div style={page}>
      <div style={header}>
        <h2 style={title}>Tour History</h2>
        <p style={subtext}>Your completed and active tour sessions</p>
      </div>

      <div style={filterRow}>
        {["All", "Active", "Completed"].map(type => (
          <button
            key={type}
            onClick={() => setFilter(type)}
            style={{
              ...filterBtn,
              background: filter === type ? "#c75028" : "#ffffff",
              color: filter === type ? "#ffffff" : "#344054",
              borderColor: filter === type ? "#c75028" : "#e1e6ed"
            }}
          >
            {type}
            <span style={{
              ...filterCount,
              color: filter === type ? "#ffffff" : "#718096"
            }}>
              {type === "All" ? history.length : history.filter(h => getStatus(h) === type).length}
            </span>
          </button>
        ))}
      </div>

      {filteredHistory.length === 0 ? (
        <div style={emptyState}>No history found</div>
      ) : (
        <div style={grid}>
          {filteredHistory.map(h => {
            const status = getStatus(h);
            const start = h.start_datetime;
            const end = h.end_datetime;
            const rate = h.guide_rate ?? h.guide_commission;

            return (
              <article
                key={h.id}
                style={card}
                onClick={() => navigate(`/guide/tour/${h.tour_session_id}`)}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = "#cbd5e1";
                  e.currentTarget.style.boxShadow = "0 5px 16px rgba(15,23,42,.07)";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = "#dce2e9";
                  e.currentTarget.style.boxShadow = "0 1px 3px rgba(15,23,42,.04)";
                }}
              >
                <div style={topRow}>
                  <h3 title={h.tour_title || "Tour session"} style={tourTitle}>{h.tour_title || "Tour session"}</h3>
                  <span style={{
                    ...badge,
                    ...(status === "Completed" ? completedBadge : activeBadge)
                  }}>
                    {status}
                  </span>
                </div>

                <div style={dateTimeBlock}>
                  <div style={dateIcon}>▦</div>
                  <div style={dateText}>
                    <span style={eyebrow}>DATE</span>
                    <strong>{formatDate(start)}</strong>
                  </div>
                </div>

                <div style={dateTimeBlock}>
                  <div style={timeIcon}>◷</div>
                  <div style={dateText}>
                    <span style={eyebrow}>TIME</span>
                    <strong>
                      {formatTime(start)}{end ? ` – ${formatTime(end)}` : ""}
                    </strong>
                  </div>
                </div>

                <div style={ratePanel}>
                  <div>
                    <span style={eyebrow}>YOUR RATE</span>
                    <div style={rateValue}>{formatRate(rate)}</div>
                  </div>
                  <span style={rateUnit}>per session</span>
                </div>

                <div style={metaRows}>
                  <div style={metaRow}>
                    <span>Assigned</span>
                    <strong>{formatDate(h.assigned_at)}</strong>
                  </div>
                  <div style={metaRow}>
                    <span>Finished</span>
                    <strong>
                      {h.unassigned_at
                        ? formatDate(h.unassigned_at)
                        : status === "Completed"
                          ? formatDate(end || start)
                          : "Still active"}
                    </strong>
                  </div>
                </div>

                <div style={divider} />
                <div style={footerText}>Session #{h.tour_session_id}</div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

const page = {
  padding: 24,
  background: "#f5f6f8",
  minHeight: "100vh",
  boxSizing: "border-box",
  color: "#172033"
};
const header = { marginBottom: 20 };
const title = {
  margin: 0, fontSize: 26, lineHeight: 1.2, fontWeight: 750,
  color: "#111827", letterSpacing: "-0.5px"
};
const subtext = { marginTop: 6, marginBottom: 0, fontSize: 14, color: "#718096" };
const filterRow = {
  display: "flex", flexWrap: "wrap", gap: 8, padding: 7, width: "fit-content",
  maxWidth: "100%", boxSizing: "border-box", marginBottom: 20,
  background: "#ffffff", border: "1px solid #dce2e9", borderRadius: 10
};
const filterBtn = {
  display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 13px",
  borderRadius: 7, border: "1px solid", cursor: "pointer", fontSize: 13,
  fontWeight: 650, transition: "background .15s ease, color .15s ease"
};
const filterCount = { fontSize: 11, fontWeight: 700 };
const grid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 360px), 1fr))",
  gap: 16, alignItems: "start"
};
const card = {
  minWidth: 0, boxSizing: "border-box", background: "#ffffff",
  borderRadius: 12, padding: 18, border: "1px solid #dce2e9",
  boxShadow: "0 1px 3px rgba(15,23,42,.04)", cursor: "pointer",
  transition: "box-shadow .15s ease, border-color .15s ease",
  WebkitTapHighlightColor: "transparent"
};
const topRow = {
  display: "flex", justifyContent: "space-between", alignItems: "flex-start",
  gap: 10, paddingBottom: 14, marginBottom: 14, borderBottom: "1px solid #e8edf2"
};
const tourTitle = {
  minWidth: 0, margin: 0, fontSize: 16, lineHeight: 1.35, fontWeight: 700,
  color: "#172033", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: "1 1 0"
};
const badge = {
  flexShrink: 0, fontSize: 11, fontWeight: 700, padding: "6px 10px",
  borderRadius: 999, lineHeight: 1, whiteSpace: "nowrap", border: "1px solid"
};
const completedBadge = { color: "#16834a", background: "#ecfdf3", borderColor: "#b7efcc" };
const activeBadge = { color: "#b66a08", background: "#fff7e8", borderColor: "#f8d99c" };
const dateTimeBlock = {
  display: "flex", alignItems: "center", gap: 11, marginBottom: 13, minWidth: 0
};
const dateIcon = {
  width: 29, height: 29, display: "grid", placeItems: "center",
  flexShrink: 0, borderRadius: 8, color: "#c75028", background: "#fff1e9", fontSize: 17
};
const timeIcon = {
  ...dateIcon, color: "#344054", background: "#f3f5f8", fontSize: 19
};
const dateText = {
  display: "flex", flexDirection: "column", gap: 3, minWidth: 0,
  color: "#172033", fontSize: 13
};
const eyebrow = {
  color: "#8490a0", fontSize: 10, fontWeight: 750,
  letterSpacing: ".07em", lineHeight: 1.2
};
const ratePanel = {
  display: "flex", justifyContent: "space-between", alignItems: "center",
  gap: 10, marginTop: 4, padding: "12px 13px",
  background: "#fff8f4", border: "1px solid #f7dfd2", borderRadius: 9
};
const rateValue = { marginTop: 3, color: "#c75028", fontSize: 21, lineHeight: 1.15, fontWeight: 750 };
const rateUnit = { color: "#8b6a5c", fontSize: 11, whiteSpace: "nowrap" };
const metaRows = { display: "grid", gap: 8, marginTop: 14 };
const metaRow = {
  display: "flex", justifyContent: "space-between", alignItems: "flex-start",
  gap: 12, fontSize: 12, lineHeight: 1.4
};
const metaRowLabel = {};
const divider = { marginTop: 13, height: 1, background: "#e8edf2" };
const footerText = { marginTop: 9, marginBottom: 0, fontSize: 11, color: "#8792a2" };
const emptyState = {
  padding: 30, background: "#ffffff", borderRadius: 11,
  border: "1px solid #dce2e9", color: "#718096", textAlign: "center"
};
